import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useView } from "../context/ViewContext";
import { getValidToken } from "../utils/auth";
import { canUseProfessionalFeatures, isPlatformAdminPayload } from "../utils/roleCapabilities";
import { useActiveBusiness } from "../hooks/useActiveBusiness";
import { WORKSPACE_STATE } from "../context/ActiveBusinessContext";
import WorkspaceUnavailable from "../components/professional/WorkspaceUnavailable";
import ProfessionalAccountRequired from "../components/professional/ProfessionalAccountRequired";
import { canAccessWorkspaceRoute } from "../utils/workspaceAuthorization";

const ALLOWED_VIEWS = new Set(["professional", "admin"]);

const ProfessionalRouteGuard = ({
  children,
  workspaceSection = null,
  providerRequired = false,
  managerCapability = null,
  workspaceAllowed = false,
  workspaceOptional = false,
}) => {
  const { currentView, setCurrentView } = useView();
  const navigate = useNavigate();
  const location = useLocation();

  const auth = getValidToken();
  const hasSession = Boolean(auth?.token);
  const role = auth?.payload?.role;
  // Keep effect deps on primitives: getValidToken() returns a new object every
  // call, so depending on auth?.payload re-fired the effect on every render.
  const isPlatformAdmin = isPlatformAdminPayload(auth?.payload);
  const { business, hydrated, workspaceState, refresh } = useActiveBusiness();
  const capabilities = business?.viewerCapabilities || null;
  const hasWorkspace = capabilities?.isWorkspaceMember === true;
  const isProfessionalRole = canUseProfessionalFeatures(role);
  const isAllowedView = ALLOWED_VIEWS.has(currentView);
  const workspaceRouteAllowed = hasWorkspace && canAccessWorkspaceRoute({
    path: location.pathname,
    capabilities,
    section: workspaceSection,
    providerRequired,
    managerCapability,
    workspaceAllowed,
  });
  const isAllowed = hasSession && (
    hasWorkspace ? workspaceRouteAllowed : isProfessionalRole || isAllowedView
  );
  const shouldSyncView =
    (isProfessionalRole || hasWorkspace) && !isAllowedView && role !== undefined;
  // Signed in, simply no professional side to this account: no professional
  // role and no workspace membership. Not a failure, and not somebody who took
  // a wrong turn inside their own workspace, so it gets neither an error nor a
  // bounce -- it gets the signup gate (design 5a).
  const noProfessionalSide = hasSession && !isAllowed && !isProfessionalRole && !hasWorkspace;
  // Unless the workspace lookup is what failed. hasWorkspace then reads false
  // for a workspace member too -- the token claims access but carries no
  // business list -- and the branch below would tell a business owner their
  // account is something it is not. A member whose global role is `customer`
  // never reaches the isProfessionalRole branch further down, so offer them the
  // same retry here.
  const workspaceLookupFailed = workspaceState === WORKSPACE_STATE.UNAVAILABLE;
  const needsProfessionalAccount = noProfessionalSide && !workspaceLookupFailed;

  useEffect(() => {
    if (!shouldSyncView) {
      return;
    }

    setCurrentView(isPlatformAdmin ? "admin" : "professional");
  }, [isPlatformAdmin, setCurrentView, shouldSyncView]);

  useEffect(() => {
    if (!hydrated || isAllowed || hasSession) {
      return;
    }

    const nextPath = `${location.pathname}${location.search}${location.hash}`;
    navigate(`/login?next=${encodeURIComponent(nextPath)}`, { replace: true });
  }, [hasSession, hydrated, isAllowed, location.hash, location.pathname, location.search, navigate]);

  useEffect(() => {
    // noProfessionalSide renders in place of the page either way, so the
    // redirect has to stand down or it bounces the visitor before they see it.
    if (!hydrated || isAllowed || !hasSession || noProfessionalSide) {
      return;
    }

    navigate(capabilities?.workspaceLandingPath || "/", { replace: true });
  }, [
    capabilities?.workspaceLandingPath,
    hasSession,
    hydrated,
    isAllowed,
    navigate,
    noProfessionalSide,
  ]);

  if (!hydrated || shouldSyncView) {
    return <div>Loading...</div>;
  }

  // A provider whose workspace has not resolved used to fall through to
  // `isProfessionalRole`, which is true, so the page rendered and dead-ended on
  // its own "Select a business workspace" message. Intercept here instead --
  // this is the single chokepoint every workspace route passes through.
  if (
    !workspaceOptional &&
    hasSession &&
    isProfessionalRole &&
    (workspaceState === WORKSPACE_STATE.UNAVAILABLE || workspaceState === WORKSPACE_STATE.EMPTY)
  ) {
    return <WorkspaceUnavailable state={workspaceState} onRetry={refresh} />;
  }

  if (isAllowed) {
    return children ?? null;
  }

  if (!hasSession) {
    return null;
  }

  if (needsProfessionalAccount) {
    return <ProfessionalAccountRequired />;
  }

  if (noProfessionalSide) {
    return <WorkspaceUnavailable state={workspaceState} onRetry={refresh} />;
  }

  // What is left is a workspace member on a page their capabilities do not
  // cover; the effect above is already moving them to their landing path.
  return (
    <div className="professional-only-message" role="alert">
      You do not have access to this workspace page.
    </div>
  );
};

export default ProfessionalRouteGuard;
